/**
 * One exclusion point for browser auth writes and account-data cleanup.
 *
 * Read/write IndexedDB transactions with overlapping object-store scopes run
 * one at a time, including across tabs. The request's success handler runs
 * while that transaction is active, so a synchronous localStorage mutation
 * there cannot overlap another participant's mutation. The database stores
 * no session or account data; it only supplies the transaction boundary.
 *
 * Every participant must use this gate, even when it also uses Web Locks.
 * Never fall back to an unguarded mutation when IndexedDB is unavailable.
 */
const DATABASE_NAME = 'kanfy-browser-auth-mutations';
const STORE_NAME = 'mutations';
const TIMEOUT_MS = 2500;

type SynchronousResult<T> = T extends PromiseLike<unknown> ? never : T;

/**
 * The callback must finish synchronously. IndexedDB cannot keep this
 * transaction active across arbitrary promises, and aborting its transaction
 * cannot undo localStorage changes the callback has already made.
 *
 * No browser API is accessed until invocation, so importing the module is
 * harmless in the native build and in environments without IndexedDB.
 */
export function withBrowserAuthMutation<T>(
  callback: () => T & SynchronousResult<T>,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let database: IDBDatabase | undefined;
    let transaction: IDBTransaction | undefined;
    let opening: IDBOpenDBRequest | undefined;
    let settled = false;
    let callbackRan = false;
    let value: T;
    const timeout = setTimeout(
      () => fail(new DOMException('Browser auth mutation timed out', 'TimeoutError')),
      TIMEOUT_MS,
    );

    const dispose = () => {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', onAbort);
      database?.close();
    };

    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      try { (transaction ?? opening?.transaction)?.abort(); } catch { /* already finished */ }
      dispose();
      reject(error);
    };

    const onAbort = () => fail(signal?.reason ?? new DOMException('Browser auth mutation aborted', 'AbortError'));
    if (signal?.aborted) {
      onAbort();
      return;
    }

    signal?.addEventListener('abort', onAbort, { once: true });

    try {
      if (typeof indexedDB === 'undefined') throw new Error('IndexedDB is unavailable for browser auth mutation');
      opening = indexedDB.open(DATABASE_NAME, 1);
      const request = opening;
      request.onerror = () => fail(request.error ?? new Error('Could not open the browser auth mutation database'));
      // A blocked open has no cancellation API. The deadline settles the
      // caller; a subsequent upgrade/success must only close the connection.
      request.onupgradeneeded = () => {
        database = request.result;
        if (settled || signal?.aborted) {
          try { request.transaction?.abort(); } catch { /* already finished */ }
          database.close();
          if (!settled) onAbort();
          return;
        }
        try {
          if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME);
        } catch (error) {
          fail(error);
        }
      };
      request.onsuccess = () => {
        database = request.result;
        if (settled || signal?.aborted) {
          database.close();
          if (!settled) onAbort();
          return;
        }
        database.onversionchange = () => fail(new DOMException('Browser auth mutation database changed', 'AbortError'));
        database.onclose = () => fail(new DOMException('Browser auth mutation database closed', 'AbortError'));
        try {
          transaction = database.transaction(STORE_NAME, 'readwrite');
          const active = transaction;
          active.onabort = () => fail(active.error ?? new DOMException('Browser auth mutation transaction aborted', 'AbortError'));
          active.onerror = () => fail(active.error ?? new Error('Browser auth mutation transaction failed'));
          active.oncomplete = () => {
            if (settled) return;
            if (signal?.aborted) {
              onAbort();
              return;
            }
            if (!callbackRan) {
              fail(new Error('Browser auth mutation transaction completed without running its callback'));
              return;
            }
            settled = true;
            dispose();
            resolve(value);
          };
          // A read in a read/write transaction is enough to acquire exclusion.
          // Running immediately after transaction() would run before its turn.
          const turn = active.objectStore(STORE_NAME).get('gate');
          turn.onerror = () => fail(turn.error ?? new Error('Could not acquire the browser auth mutation transaction'));
          turn.onsuccess = () => {
            if (settled || signal?.aborted) {
              if (!settled) onAbort();
              return;
            }
            try {
              const result = callback();
              if (result != null &&
                  (typeof result === 'object' || typeof result === 'function') &&
                  typeof (result as { then?: unknown }).then === 'function') {
                // A rejected promise returned by a mistaken async callback
                // must not become an additional unhandled rejection.
                void Promise.resolve(result).catch(() => {});
                throw new TypeError('Browser auth mutation callback must be synchronous');
              }
              value = result;
              callbackRan = true;
            } catch (error) {
              fail(error);
            }
          };
        } catch (error) {
          fail(error);
        }
      };
    } catch (error) {
      fail(error);
    }
  });
}
